var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useEffect, useRef, useMemo, useCallback, } from 'react';
import styled, { css } from 'styled-components';
import { GroupContext } from '../../CardSelectionGroup';
import { TextBody } from '../../../Typography';
import { Icon } from '../../../Icon';
import { CardBody } from './CardBody';
import { SelectCircle } from '../../SelectCircle';
import { CardContainer } from '../CardContainer';
import { useMediaSize } from '../../../tools/utils';
const InnerCardContainer = styled.span `
  ${({ align, theme: { space } }) => css `
    display: flex;
    flex-direction: ${align === 'inline' ? 'column' : 'row'};
    align-items: center;
    justify-content: center;
    gap: ${align === 'inline' ? space.xxs : space.sm};
  `}
`;
const StyledIconWrapper = styled.span `
  ${({ hideIconSmDown, align, isMdUp, }) => css `
    display: ${hideIconSmDown && !isMdUp ? 'none' : 'inline-block'};
    ${align === 'stacked' ? 'align-self: flex-start;' : ''}
  `}
`;
const CardContentWrapper = styled.span `
  display: flex;
  flex-direction: column;
`;
const CardTitleText = styled(TextBody) `
  color: ${({ theme: { colors } }) => colors.bluesuedeshoes};
`;
const CardTitle = ({ title, }) => {
    if (title)
        return (React.createElement(CardTitleText, { tag: "p", size: "md", weight: "bold" }, title));
    return null;
};
const CardSelectionCard = (_a) => {
    var { cardId, ariaLabel, ariaLabelDescription, title, description, icon } = _a, others = __rest(_a, ["cardId", "ariaLabel", "ariaLabelDescription", "title", "description", "icon"]);
    const { align, variant, selectedKeys, showError, customErrorId, childIndex, onSelected, onKeyDown, hideIconSmDown, defaultSelection, inlineCardWidth, inlineCardAlignment, } = useContext(GroupContext);
    const isDefault = useCallback(() => {
        if (typeof defaultSelection === 'string') {
            return cardId === defaultSelection;
        }
        if (Array.isArray(defaultSelection)) {
            return defaultSelection.includes(cardId);
        }
        return false;
    }, [defaultSelection, cardId]);
    const cardRef = useRef(null);
    const index = useMemo(() => childIndex(cardId), [childIndex, cardId]);
    const isSelected = useMemo(() => selectedKeys.includes(cardId), [selectedKeys, cardId]);
    /**
     * The following conditions must be true for focus to be skipped:
     *
     * 1- `isSelected` must be `true`
     * 2- `isDefault` must be `true`
     * 3- `isFocusSkipped` must be `false`
     *
     * Thus, the first render in which `isSelected` is true, if the rest of the conditions
     * above are met, then focus will be skipped, and on subsequent renders in which
     * the value of `isSelected` has changed _and_ `isSelected` is `true`, focus will be
     * called on the card.
     */
    const [isFocusSkipped, setIsFocusSkipped] = React.useState(false);
    const shouldSkipFocus = isDefault() && !isFocusSkipped;
    useEffect(() => {
        var _a;
        if (isSelected && !shouldSkipFocus)
            (_a = cardRef === null || cardRef === void 0 ? void 0 : cardRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (isSelected && shouldSkipFocus)
            setIsFocusSkipped(true);
    }, [isSelected]);
    useEffect(() => {
        var _a;
        /**
         * On error: focus on selected card (if one is selected), or the first card in the group
         */
        if (showError && (isSelected || (!selectedKeys.length && index === 0))) {
            (_a = cardRef === null || cardRef === void 0 ? void 0 : cardRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [showError]);
    const tabIndex = () => {
        if (variant === 'single-select') {
            return (!selectedKeys.length && index === 0) || isSelected ? 0 : -1;
        }
        return 0;
    };
    const isMdUp = useMediaSize('MdUp');
    const innerCardRef = React.useRef(null);
    const role = variant === 'single-select' ? 'radio' : 'checkbox';
    const safeDescription = typeof description === 'string' ? description : '';
    const safeAriaLabel = ariaLabel || `${title}: ${ariaLabelDescription || safeDescription}`;
    return (React.createElement(CardContainer, Object.assign({ ref: cardRef, "$align": align, "$isSelected": isSelected, "$showError": showError, onClick: () => onSelected(cardId), onKeyDown: (e) => {
            onKeyDown(e, cardId);
        } }, others, { type: "button", role: role, "aria-checked": isSelected, "aria-label": safeAriaLabel, "aria-labelledby": showError ? customErrorId : undefined, tabIndex: tabIndex(), "$inlineCardWidth": inlineCardWidth, "$inlineCardAlignment": inlineCardAlignment, "$hasDescription": !!description }),
        React.createElement(SelectCircle, { variant: variant, size: align === 'inline' ? 'md' : 'xl', hasIcon: !!icon, isTopAligned: !!description, align: align, isSelected: isSelected }),
        React.createElement(InnerCardContainer, { ref: innerCardRef, align: align },
            icon && (React.createElement(StyledIconWrapper, { align: align, hideIconSmDown: hideIconSmDown, isMdUp: isMdUp },
                React.createElement(Icon, { icon: icon, size: "xl", fill: "bluesuedeshoes" }))),
            React.createElement(CardContentWrapper, { icon: icon, align: align },
                React.createElement(CardTitle, { title: title }),
                React.createElement(CardBody, { description: description, align: align })))));
};
export { CardSelectionCard };
//# sourceMappingURL=CardSelectionCard.js.map